import {
  LayoutDashboard,
  Store,
  Users,
  Building2,
  Stethoscope,
  Pill,
  Layers,
  Tags,
  Truck,
  Smartphone,
  Wallet,
  BarChart3,
  Receipt,
  Search as SearchIcon,
  Settings,
  Lock,
  Handshake,
} from "lucide-react";

export const ADMIN_CONFIG_PATH = "/admin/configuration";

export const ADMIN_STANDALONE_ITEMS = [
  { icon: LayoutDashboard, labelKey: "nav:admin.dashboard", path: "/admin", exact: true },
];

export const ADMIN_NAV_GROUPS = [
  {
    id: "network",
    labelKey: "nav:admin.network",
    items: [
      { icon: Store, labelKey: "nav:admin.myPharmacies", path: "/admin/my-pharmacies" },
      { icon: Building2, labelKey: "nav:admin.pharmacies", path: "/admin/pharmacies" },
      { icon: Users, labelKey: "nav:admin.clients", path: "/admin/clients" },
      { icon: Stethoscope, labelKey: "nav:admin.doctors", path: "/admin/doctors" },
      { icon: Handshake, labelKey: "nav:admin.kam", path: "/admin/kam-users" },
    ],
  },
  {
    id: "catalog",
    labelKey: "nav:admin.catalog",
    items: [
      { icon: Pill, labelKey: "nav:admin.medicines", path: "/admin/medicines" },
      { icon: Layers, labelKey: "nav:admin.forms", path: "/admin/medicine-forms" },
      { icon: Tags, labelKey: "nav:admin.categories", path: "/admin/medicine-categories" },
    ],
  },
  {
    id: "ops",
    labelKey: "nav:admin.ops",
    items: [
      { icon: Truck, labelKey: "nav:admin.deliveries", path: "/admin/deliveries", badgeKey: "deliveries" },
      { icon: Smartphone, labelKey: "nav:admin.payments", path: "/admin/payments", badgeKey: "payments" },
      { icon: Wallet, labelKey: "nav:admin.kamCommissions", path: "/admin/kam-commissions" },
      { icon: Wallet, labelKey: "nav:admin.doctorCommissions", path: "/admin/doctor-commissions" },
    ],
  },
  {
    id: "subscriptions",
    labelKey: "nav:admin.subscriptions",
    items: [
      { icon: BarChart3, labelKey: "nav:admin.subscriptionStats", path: "/admin/subscription-stats" },
      { icon: Building2, labelKey: "nav:admin.subscriptionPharmacies", path: "/admin/subscription-pharmacies" },
      { icon: Receipt, labelKey: "nav:admin.paymentHistory", path: "/admin/payment-history" },
    ],
  },
  {
    id: "reports",
    labelKey: "nav:admin.reports",
    items: [
      { icon: SearchIcon, labelKey: "nav:admin.searchAnalytics", path: "/admin/medicine-stats" },
    ],
  },
];

export const ADMIN_CONFIG_LINK = {
  icon: Settings,
  labelKey: "nav:admin.configuration",
  path: ADMIN_CONFIG_PATH,
};

export const ADMIN_ACCOUNT_ITEMS = [
  { icon: Lock, labelKey: "nav:admin.password", path: "/admin/change-password" },
];

export const CONFIG_SECTIONS = [
  {
    id: "site",
    labelKey: "nav:config.site",
    path: "/admin/configuration/site",
    module: "settings",
  },
  {
    id: "monetization",
    labelKey: "nav:config.monetization",
    children: [
      {
        labelKey: "nav:config.paymentAccounts",
        path: "/admin/configuration/monetization/accounts",
        module: "payment_config",
      },
      {
        labelKey: "nav:config.plansMm",
        path: "/admin/configuration/monetization/plans-mm",
        module: "plans",
      },
      {
        labelKey: "nav:config.plansStripe",
        path: "/admin/configuration/monetization/plans-stripe",
        module: "subscription_management",
      },
    ],
  },
  {
    id: "commissions",
    labelKey: "nav:config.commissions",
    children: [
      {
        labelKey: "nav:config.kamCommission",
        path: "/admin/configuration/commissions/kam",
        module: "kam_settings",
      },
      {
        labelKey: "nav:config.doctorCommission",
        path: "/admin/configuration/commissions/doctor",
        module: "doctor_commission_settings",
      },
    ],
  },
  {
    id: "delivery",
    labelKey: "nav:config.delivery",
    path: "/admin/configuration/delivery",
    module: "delivery_pricing",
  },
  {
    id: "access",
    labelKey: "nav:config.access",
    children: [
      {
        labelKey: "nav:config.adminUsers",
        path: "/admin/configuration/access/users",
        module: "admin_users",
      },
      {
        labelKey: "nav:config.roles",
        path: "/admin/configuration/access/roles",
        module: "admin_roles",
      },
    ],
  },
];

/** Flat list of all configuration tab paths. */
export function getAllConfigPaths() {
  const paths = [];
  for (const section of CONFIG_SECTIONS) {
    if (section.path) {
      paths.push(section.path);
    }
    if (section.children) {
      for (const child of section.children) {
        paths.push(child.path);
      }
    }
  }
  return paths;
}

export function isConfigurationPath(pathname) {
  return pathname === ADMIN_CONFIG_PATH || pathname.startsWith(`${ADMIN_CONFIG_PATH}/`);
}

export function canAccessConfigModule(user, moduleCode, canReadModule) {
  if (!user || user.role !== "admin") return false;
  if (user.admin_is_super) return true;
  return canReadModule(user, moduleCode);
}

export function getVisibleConfigSections(user, canReadModule) {
  return CONFIG_SECTIONS.map((section) => {
    if (section.path) {
      if (!canAccessConfigModule(user, section.module, canReadModule)) return null;
      return section;
    }
    const children = section.children.filter((child) =>
      canAccessConfigModule(user, child.module, canReadModule)
    );
    if (children.length === 0) return null;
    return { ...section, children };
  }).filter(Boolean);
}

export function getDefaultConfigurationPath(user, canReadModule) {
  for (const section of CONFIG_SECTIONS) {
    if (section.path && canAccessConfigModule(user, section.module, canReadModule)) {
      return section.path;
    }
    if (section.children) {
      for (const child of section.children) {
        if (canAccessConfigModule(user, child.module, canReadModule)) {
          return child.path;
        }
      }
    }
  }
  return null;
}

export function findActiveConfigSection(pathname) {
  for (const section of CONFIG_SECTIONS) {
    if (section.path && pathname.startsWith(section.path)) {
      return section;
    }
    if (section.children?.some((c) => pathname.startsWith(c.path))) {
      return section;
    }
  }
  return null;
}

/** Section active avec sous-onglets filtrés selon RBAC. */
export function findVisibleActiveConfigSection(pathname, user, canReadModule) {
  const match = findActiveConfigSection(pathname);
  if (!match) return null;
  const visible = getVisibleConfigSections(user, canReadModule);
  return visible.find((section) => section.id === match.id) || null;
}

export function pathMatchesNavItem(pathname, item) {
  if (item.exact) {
    return pathname === item.path;
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function findActiveNavGroupId(pathname) {
  if (isConfigurationPath(pathname)) return null;
  for (const group of ADMIN_NAV_GROUPS) {
    if (group.items.some((item) => pathMatchesNavItem(pathname, item))) {
      return group.id;
    }
  }
  return null;
}

export function getVisibleNavGroups(user, canAccessAdminPath) {
  return ADMIN_NAV_GROUPS.map((group) => {
    const items = group.items.filter((item) => canAccessAdminPath(user, item.path));
    if (items.length === 0) return null;
    return { ...group, items };
  }).filter(Boolean);
}
